import { useEffect, useMemo, useRef } from 'react'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'

import { useCADPageStore } from '@/state'
import { useShallow } from 'zustand/react/shallow'
import { useCADQuery } from '@/services/queries/cads'
import { ReuploadCadButton } from '@/pages/DocumentPage/components/ReuploadCadButton'
import { cn } from '@/utils'
import { Button } from '@/components/ui/button'
import { ControlPanel } from '../ControlPanel'
import { ScreenshotPanelButton } from '../ScreenshotPanelButton'
import { ScreenshotOverlay } from '../CADScene/ScreenshotOverlay'
import { CADPartLabel } from '../CADScene/CADPartLabel'
import { useDocumentPageQuery } from '@/pages/DocumentPage/queries'
import { useCanvasCamera, useCanvasContext } from '@/lib/cad/CanvasProvider'

export const CADEditor = () => {
  const { isLoading, data } = useCADQuery()
  const { data: docData } = useDocumentPageQuery()
  const resetEditMode = useCADPageStore(
    useShallow((state) => state.resetEditMode),
  )
  const isEditModeActive = useCADPageStore(
    useShallow((state) => state.isEditModeActive),
  )
  const resetRetakeMode = useCADPageStore(
    useShallow((state) => state.resetRetakeMode),
  )
  const isRetakeModeActive = useCADPageStore(
    useShallow((state) => state.isRetakeModeActive),
  )
  const resetCaptureMode = useCADPageStore(
    useShallow((state) => state.resetCaptureMode),
  )
  const isCapturingScreenshotFromDocumentPage = useCADPageStore(
    useShallow((state) => state.isCapturingScreenshotFromDocumentPage),
  )

  const activeOperationStep = useCADPageStore((state) => state.operationStep)

  const cadVersionId = data.version?.id
  const documentType = docData?.documentType
  const selectFromCad = useMemo(
    () => activeOperationStep?.selectFromCad,
    [activeOperationStep?.selectFromCad],
  )

  const camera = useCanvasCamera()
  const { setCanvasBounds, setCanvasVisible } = useCanvasContext()
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    resetEditMode()
  }, [resetEditMode])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (isCapturingScreenshotFromDocumentPage) {
          resetCaptureMode()
        } else if (isRetakeModeActive) {
          resetRetakeMode()
        } else if (isEditModeActive) {
          resetEditMode()
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [
    isCapturingScreenshotFromDocumentPage,
    isRetakeModeActive,
    isEditModeActive,
    resetCaptureMode,
    resetRetakeMode,
    resetEditMode,
  ])

  useEffect(() => {
    setCanvasVisible(true)
    return () => setCanvasVisible(false)
  }, [setCanvasVisible])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const updateBounds = () => {
      const rect = container.getBoundingClientRect()
      setCanvasBounds({
        x: rect.left,
        y: rect.top,
        width: rect.width,
        height: rect.height,
      })
    }

    updateBounds()

    const resizeObserver = new ResizeObserver(updateBounds)
    resizeObserver.observe(container)

    window.addEventListener('scroll', updateBounds, true)
    window.addEventListener('resize', updateBounds)

    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('scroll', updateBounds, true)
      window.removeEventListener('resize', updateBounds)
      setCanvasBounds(null)
    }
  }, [setCanvasBounds])

  if (data.hasNoDownloadUrl && !isLoading) {
    return (
      <div className="flex flex-col mt-16 items-center">
        <ExclamationTriangleIcon className="h-8 w-8 text-red-500" />
        <h1 className="text-lg">Uploading CAD file was unsuccessful.</h1>
        <h2 className="text-lg">Please attempt to upload again.</h2>
        <div className="mt-6">
          {cadVersionId && <ReuploadCadButton cadVersionId={cadVersionId} />}
        </div>
      </div>
    )
  }

  return (
    <div className="relative w-full h-full pl-4">
      <div
        ref={containerRef}
        className={cn(
          'relative w-full h-full border-[3px] rounded-md border-transparent',
          {
            'border-green-400': isEditModeActive && !selectFromCad,
            'border-purple-600': selectFromCad,
            'border-primary-50':
              isRetakeModeActive || isCapturingScreenshotFromDocumentPage,
          },
        )}
      >
        {(isEditModeActive ||
          isRetakeModeActive ||
          isCapturingScreenshotFromDocumentPage) && (
          <Button
            variant="outline"
            size="sm"
            className={cn('absolute top-0 right-0 bg-white border-2 z-50 h-5', {
              'border-green-400': isEditModeActive && !selectFromCad,
              'border-purple-600': selectFromCad,
              'text-green-400': isEditModeActive && !selectFromCad,
              'text-purple-600': selectFromCad,
              'hover:text-green-400': !selectFromCad,
              'hover:text-purple-600': selectFromCad,
              'primary-50':
                isRetakeModeActive || isCapturingScreenshotFromDocumentPage,
              'border-primary-50':
                isRetakeModeActive || isCapturingScreenshotFromDocumentPage,
              'text-primary-50':
                isRetakeModeActive || isCapturingScreenshotFromDocumentPage,
              'hover:text-primary-50':
                isRetakeModeActive || isCapturingScreenshotFromDocumentPage,
            })}
            onClick={
              isCapturingScreenshotFromDocumentPage
                ? resetCaptureMode
                : isRetakeModeActive
                  ? resetRetakeMode
                  : resetEditMode
            }
            style={{ translate: '-30% -50%' }}
          >
            ESC
          </Button>
        )}
        <ScreenshotOverlay />
        {documentType && <CADPartLabel documentType={documentType} />}
        {camera && <ScreenshotPanelButton camera={camera} />}
        <ControlPanel />
      </div>
    </div>
  )
}
